import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Typography, TextField, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Alert, CircularProgress } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AdminTargets() {
  const [serviceCodes, setServiceCodes] = useState([]); // [{_id, id, name, longName}]
  const [matrix, setMatrix] = useState([]); // [{ row: 1, targets: { [serviceCode]: number } }, ...]
  const [editMatrix, setEditMatrix] = useState([]); // same shape as matrix
  // DEBUG: Log state for troubleshooting
  console.log('serviceCodes', serviceCodes);
  console.log('matrix', matrix);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  // Only fetch on mount
  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/serviceCodes`),
      axios.get(`${API_URL}/api/service-code-targets`)
    ])
      .then(([codesRes, matrixRes]) => {
        setServiceCodes(codesRes.data);
        setMatrix(matrixRes.data);
        setEditMatrix(matrixRes.data);
      })
      .catch(() => setError('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  const handleInput = (rowIdx, codeId, value) => {
    setEditMatrix(prev => prev.map((row, i) =>
      i === rowIdx ? { ...row, targets: { ...(row.targets || {}), [codeId]: value } } : row
    ));
  };


  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/service-code-targets`, editMatrix);
      setMsg('All targets saved!');
      // Re-fetch from backend after save
      const matrixRes = await axios.get(`${API_URL}/api/service-code-targets`);
      setMatrix(matrixRes.data);
      setEditMatrix(matrixRes.data);
    } catch (e) {
      setError('Failed to save targets');
    } finally {
      setSaving(false);
    }
  };

  const getColBg = idx => (idx % 2 === 0 ? '#f7f7fa' : '#e7f0fa');

  // --- CSV Export ---
  const handleExportCSV = () => {
    if (!serviceCodes.length || !editMatrix.length) return;
    // Header: Row, id - name for each code
    const header = ['Row', ...serviceCodes.map(code => `${code.id} - ${code.name || code.longName || ''}`.replace(/ - $/, ''))];
    const codeIds = serviceCodes.map(code => code.id);
    const rows = editMatrix.map(row => [row.row, ...codeIds.map(id => (row.targets?.[id] ?? ''))]);
    const csv = [header, ...rows].map(arr => arr.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'service_code_targets.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // --- CSV Import ---
  const handleImportCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const text = evt.target.result;
        const lines = text.split(/\r?\n/).filter(Boolean);
        const [headerLine, ...dataLines] = lines;
        const header = headerLine.split(',').map(h => h.replace(/^"|"$/g, ''));
        // Match header columns to serviceCodes by id (allow id - name or just id)
        const headerCodes = header.slice(1).map(h => h.split(' - ')[0]);
        const codeIds = serviceCodes.map(code => code.id);
        if (headerCodes.length !== codeIds.length || !headerCodes.every((h, i) => h === codeIds[i])) {
          setError('CSV columns do not match current service codes.');
          return;
        }
        const newMatrix = dataLines.map((line, i) => {
          const cells = line.split(',').map(cell => cell.replace(/^"|"$/g, ''));
          const rowNum = Number(cells[0]);
          const targets = {};
          headerCodes.forEach((id, idx) => {
            const val = cells[idx + 1];
            if (id && val !== undefined && val !== '') targets[id] = Number(val);
          });
          return { row: rowNum, targets };
        });
        if (newMatrix.length !== 40) {
          setError('CSV must have exactly 40 rows.');
          return;
        }
        setEditMatrix(newMatrix);
        setMsg('CSV imported! Review and Save All to apply.');
      } catch (err) {
        setError('Failed to parse CSV.');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
        Admin: Target Quantities Matrix
      </Typography>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveAll}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          Save All
        </Button>
        <Button variant="outlined" onClick={handleExportCSV} disabled={!serviceCodes.length || !editMatrix.length}>
          Export CSV
        </Button>
        <Button variant="outlined" component="label">
          Import CSV
          <input type="file" accept=".csv" hidden onChange={handleImportCSV} />
        </Button>
      </div>
      {msg && <Alert sx={{ mb: 2 }} severity="success">{msg}</Alert>}
      {error && <Alert sx={{ mb: 2 }} severity="error">{error}</Alert>}
      <TableContainer component={Paper} sx={{ borderRadius: 3, mb: 2, maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, zIndex: 2, background: '#fff' }}>#</TableCell>
              {serviceCodes.map((code, idx) => (
                <TableCell
                  key={code._id || code.id || idx}
                  sx={{
                    fontWeight: 700,
                    background: getColBg(idx),
                    minWidth: 120,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                  }}
                  align="center"
                >
                  {`${code.id} - ${code.name || code.longName || ''}`.replace(/ - $/, '')}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {editMatrix.slice(0, 1).map((row, rowIdx) => (
              <TableRow key={row.row}>
                <TableCell sx={{ fontWeight: 700, background: '#fff', position: 'sticky', left: 0, zIndex: 1 }}>{row.row}</TableCell>
                {serviceCodes.map((code, colIdx) => (
                  <TableCell
                    key={code._id || code.id || colIdx}
                    sx={{ background: getColBg(colIdx), minWidth: 120 }}
                    align="center"
                  >
                    <TextField
                      type="number"
                      size="small"
                      value={(row.targets?.[code.id]) ?? ''}
                      onChange={e => handleInput(rowIdx, code.id, e.target.value)}
                      inputProps={{ style: { textAlign: 'center' } }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSaveAll}
        disabled={saving || loading}
        startIcon={saving ? <CircularProgress size={20} /> : null}
      >
        Save All
      </Button>
    </div>
  );
}
